import {Presenter} from '../../../../Support/with_presenter';
import {loadingResponse, NetworkRequestStatus, NetworkResult} from '../../../../Network/NetworkResult';
import {SnippetService} from '../../../../Network/SnippetService';
import {DateRange} from '../../../../Domain/DateRange';
import {Snippet} from '../../../../Models/Snippet';
import {ReportService} from '../../../../Network/ReportService';
import {ReportTemplate, TrendTemplate} from './Models/ReportTemplate';
import {Report} from '../../../../Models/Report';
import {SplitStrategy, Strategy} from '../../../../Business/calculateSplitTimes';
import {Fraction} from '../../../../Domain/Fraction';

export interface ReportData {
    snippets: Snippet[];
}

export class TrendReportPresenter implements Presenter {
    public mode: 'comparing' | 'trend' = 'trend';

    public result: NetworkResult<ReportData> = loadingResponse('Fetching snippets...');

    public selectedSnippetIds: number[] = [];
    public highlightedSnippetIds: number[] = [];

    public splitStrategy: SplitStrategy = {
        strategy: Strategy.Fraction,
        fraction: Fraction.fromPercentage(100),
    };

    public isSaving = false;

    public name: string = '';
    public description: string = '';
    public isLocked: boolean = false;

    public isEditingName = false;
    public adding: boolean = false;
    public deleting: boolean = false;

    public template: ReportTemplate;

    public get selectedSnippets(): Snippet[] {
        if (this.result.status !== 'success') {
            return [];
        }

        const snippets = this.result.data.snippets;
        return this.selectedSnippetIds.map((id) => snippets.find((snippet) => snippet.id === id)!);
    }

    constructor(
        private report: Report,
        private dateRange: DateRange | null,
        private snippetService: SnippetService,
        private reportService: ReportService,
    ) {
        this.name = report.name;
        this.description = report.description;
        this.isLocked = report.is_locked;
        this.template = report.template;
    }

    public async getSnippets(): Promise<void> {
        const snippetsResult = await this.snippetService.getFiltered(
            this.report.snippet_collection_ids,
            this.report.user_ids,
            this.report.crew_ids,
            this.dateRange,
        );
        if (
            snippetsResult.status === NetworkRequestStatus.Error ||
            snippetsResult.status === NetworkRequestStatus.Loading
        ) {
            this.result = snippetsResult;
            return;
        }
        if (snippetsResult.data.length === 0) {
            this.result = {
                status: NetworkRequestStatus.Success,
                data: {
                    snippets: [],
                },
            };
            return;
        }

        this.result = {
            status: NetworkRequestStatus.Success,
            data: {
                snippets: snippetsResult.data,
            },
        };
    }

    public async mount(): Promise<void> {
        this.getSnippets();
    }

    public unmount(): void {
        /* Noop */
    }

    public async submit(): Promise<void> {
        this.isEditingName = false;
        this.isSaving = true;
        try {
            await this.reportService.update(this.report.id!, this.name, this.description, this.isLocked, this.template);
        } catch (e) {
            console.error(e);
        } finally {
            this.isSaving = false;
        }
    }

    public startAddingTrend() {
        this.adding = true;
    }

    public stopAddingTrend() {
        this.adding = false;
    }

    public startEditName() {
        this.isEditingName = true;
    }

    public stopEditName() {
        this.isEditingName = false;
    }

    public async updateName(name: string) {
        this.name = name;
    }

    public changeSplitStrategy(splitStrategy: SplitStrategy) {
        this.splitStrategy = splitStrategy;
    }

    public changeMode(mode: 'comparing' | 'trend') {
        this.mode = mode;
    }

    private updateDescriptionTimer: number | null = null;

    public updateDescription(description: string) {
        this.description = description;

        if (this.updateDescriptionTimer) {
            clearTimeout(this.updateDescriptionTimer);
        }
        this.updateDescriptionTimer = window.setTimeout(async () => {
            await this.submit();
        }, 500);
    }

    public async toggleLock(): Promise<void> {
        this.isLocked = !this.isLocked;

        if (this.isLocked) {
            this.adding = false;
            this.isEditingName = false;
        }

        await this.submit();
    }

    public updateSelectedSnippetIds(snippetIds: number[]): void {
        this.selectedSnippetIds = snippetIds;
    }

    public updateHighlightedSnippetIds(snippetIds: number[]): void {
        this.highlightedSnippetIds = snippetIds;
    }

    public async addTrendTemplate(trendTemplate: TrendTemplate): Promise<void> {
        this.template = {
            ...this.template,
            trends: [...this.template.trends, trendTemplate],
        };
        this.adding = false;
        await this.submit();
    }

    public async updateTrendTemplate(index: number, trendTemplate: TrendTemplate): Promise<void> {
        const trends = [...this.template.trends];
        trends[index] = trendTemplate;
        this.template = {
            ...this.template,
            trends,
        };
        await this.submit();
    }

    public async deleteTrendTemplate(index: number): Promise<void> {
        const trends = [...this.template.trends];
        trends.splice(index, 1);
        this.template = {
            ...this.template,
            trends,
        };
        await this.submit();
    }

    public async delete(): Promise<void> {
        if (!this.report.id) {
            return;
        }
        try {
            this.deleting = true;
            await this.reportService.delete(this.report.id);
            window.location.href = '/reports';
        } finally {
            this.deleting = false;
        }
    }

    public async removeSnippet(snippetId: number): Promise<void> {
        try {
            this.deleting = true;
            await this.snippetService.delete(String(snippetId));
            if (this.result.status === NetworkRequestStatus.Success) {
                //When things are successful, we can safely assume that the snippet is removed from the server
                this.result = {
                    status: NetworkRequestStatus.Success,
                    data: {
                        snippets: this.result.data.snippets.filter((snippet) => {
                            return '' + snippet.id !== '' + snippetId;
                        }),
                    },
                };
            }
        } finally {
            this.deleting = false;
        }
    }
}
